import { useId, useState } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

import { Button, Checkbox, Dialog, Field } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { ngayVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { useSuaLicense } from '../../hooks/useLicense';
import { kiemGiaHan } from '../../nghiep-vu/license';
import type { LicenseRpa } from '../../types';

/**
 * Gia hạn License (`LicenseRenewDialog` của native): đổi hạn của chính License đang có, không tạo License mới. License
 * đã hết hạn được máy chủ mở lại `active` khi hạn mới ở tương lai.
 */
export function HopGiaHan({ license, onClose }: { license: LicenseRpa; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  const sua = useSuaLicense();
  const [han, setHan] = useState(license.is_lifetime ? '' : String(license.expired_at ?? '').slice(0, 10));
  const [vinhVien, setVinhVien] = useState(license.is_lifetime);
  const [loi, setLoi] = useState<'expiryRequired' | 'expiryPast' | null>(null);
  const ten = license.opencore_username || license.opencore_user_id || license.project_id;

  const gui = () => {
    const thay = kiemGiaHan(han, vinhVien);
    setLoi(thay);
    if (thay || sua.isPending) return;
    sua.mutate(
      { id: license.id, than: { isLifetime: vinhVien, expiredAt: vinhVien ? null : han } },
      {
        onSuccess: (l) => {
          toast.success(t(l.access_sync_status === 'error' ? 'rpaLicense.giaHan.xongHongDongBo' : 'rpaLicense.giaHan.xong'));
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width={480}
      destroyOnHidden
      title={t('rpaLicense.giaHan.tieuDe', { ten })}
      footer={
        <div className="space-y-3">
          {sua.error && <LoiTrongHop>{dichLoi(sua.error)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={sua.isPending}>
              {sua.isPending ? t('chung.dangLuu') : t('rpaLicense.giaHan.nut')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <p className="text-sm text-content-secondary">
          {t('rpaLicense.giaHan.hienTai', { ngay: license.is_lifetime ? t('rpaLicense.vinhVien') : ngayVN(license.expired_at) })}
        </p>
        <Field
          label={t('rpaLicense.giaHan.ngayMoi')}
          required={!vinhVien}
          error={loi ? t(loi === 'expiryPast' ? 'rpaLicense.giaHan.loiQuaKhu' : 'rpaLicense.giaHan.loiThieu') : undefined}
        >
          {(p) => (
            <DatePicker
              id={p.id}
              aria-invalid={p['aria-invalid']}
              aria-describedby={p['aria-describedby']}
              status={p['aria-invalid'] ? 'error' : undefined}
              className="w-full"
              format="DD/MM/YYYY"
              disabled={vinhVien}
              disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
              value={han ? dayjs(han) : null}
              onChange={(d) => {
                setHan(d ? d.format('YYYY-MM-DD') : '');
                setLoi(null);
              }}
            />
          )}
        </Field>
        <Checkbox
          checked={vinhVien}
          onChange={(e) => {
            setVinhVien(e.target.checked);
            setLoi(null);
          }}
        >
          {t('rpaLicense.form.vinhVien')}
        </Checkbox>
        <p className="text-xs text-content-muted">{t('rpaLicense.giaHan.goiY')}</p>
      </form>
    </Dialog>
  );
}
